import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Briefcase, Send, Eye, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import projectsService from "../services/projectsService";
import { classifyWorkflowError } from "../lib/workflowErrors";

const emptyForm = {
  project_id: "",
  title: "",
  description: "",
  scope_of_work: "",
  trade: "",
  csi_code: "",
  location_text: "",
  city: "",
  state: "",
  zip_code: "",
  estimated_value_min: "",
  estimated_value_max: "",
  start_date: "",
  end_date: "",
  response_deadline: "",
  visibility: "public",
  status: "draft",
};

const normalizeDateTime = (value) => (value ? new Date(value).toLocaleString() : "Not set");

const MarketplacePage = () => {
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteForm, setInviteForm] = useState({ subcontractor_company_id: "", contact_email: "" });
  const [selectedPost, setSelectedPost] = useState(null);
  const [editingPostId, setEditingPostId] = useState(null);
  const [filters, setFilters] = useState({ status: "", search: "", project_id: "", trade: "", state: "" });
  const [interests, setInterests] = useState([]);
  const [bookingForms, setBookingForms] = useState({});
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      setProjects(await projectsService.getAll());
    } catch {
      setProjects([]);
    }
  };

  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API}/contractors`);
      setContractors(response.data || []);
    } catch {
      setContractors([]);
    }
  };

  const fetchPosts = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
      const response = await axios.get(`${API}/marketplace/opportunities`, { params });
      setPosts(response.data || []);
      if (!selectedPost && response.data?.length) setSelectedPost(response.data[0]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load marketplace opportunities");
    }
  };

  const fetchInterests = async (post) => {
    if (!post?.id) return;
    try {
      const response = await axios.get(`${API}/marketplace/opportunities/${post.id}/interests`);
      setInterests(response.data || []);
    } catch {
      setInterests([]);
    }
  };

  const fetchInvites = async (post) => {
    if (!post?.id) return;
    try {
      const response = await axios.get(`${API}/marketplace/opportunities/${post.id}/invites`);
      setInvites(response.data || []);
    } catch {
      setInvites([]);
    }
  };

  useEffect(() => { fetchProjects(); fetchContractors(); }, []);
  useEffect(() => { fetchPosts(); }, [filters.status, filters.project_id]);
  useEffect(() => { fetchInterests(selectedPost); fetchInvites(selectedPost); }, [selectedPost?.id]);

  const projectName = (projectId) => projects.find((project) => project.id === projectId)?.name || "Unlinked project";
  const handleChange = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const startEditPost = (post) => {
    setEditingPostId(post.id);
    setForm({
      project_id: post.project_id || "",
      title: post.title || "",
      description: post.description || "",
      scope_of_work: post.scope_of_work || "",
      trade: post.trade || "",
      csi_code: post.csi_code || "",
      location_text: post.location_text || "",
      city: post.city || "",
      state: post.state || "",
      zip_code: post.zip_code || "",
      estimated_value_min: post.estimated_value_min || "",
      estimated_value_max: post.estimated_value_max || "",
      start_date: post.start_date ? post.start_date.slice(0, 16) : "",
      end_date: post.end_date ? post.end_date.slice(0, 16) : "",
      response_deadline: post.response_deadline ? post.response_deadline.slice(0, 16) : "",
      visibility: post.visibility || "public",
      status: post.status || "draft",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingPostId(null);
    setForm(emptyForm);
  };

  const submitPost = async (e, publish = false) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        status: publish ? "published" : form.status,
        project_id: form.project_id || null,
        estimated_value_min: form.estimated_value_min ? Number(form.estimated_value_min) : null,
        estimated_value_max: form.estimated_value_max ? Number(form.estimated_value_max) : null,
        requirements_json: {},
        documents_json: [],
      };
      const response = editingPostId
        ? await axios.put(`${API}/marketplace/opportunities/${editingPostId}`, payload)
        : await axios.post(`${API}/marketplace/opportunities`, payload);
      if (publish && editingPostId) {
        await axios.post(`${API}/marketplace/opportunities/${editingPostId}/publish`);
      }
      resetForm();
      await fetchPosts();
      setSelectedPost(response.data);
      toast.success(editingPostId ? "Opportunity updated" : (publish ? "Opportunity published" : "Draft saved"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save opportunity");
    } finally {
      setLoading(false);
    }
  };

  const publishPost = async (post) => {
    try {
      const response = await axios.post(`${API}/marketplace/opportunities/${post.id}/publish`);
      await fetchPosts();
      setSelectedPost(response.data);
      toast.success("Opportunity published");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish opportunity");
    }
  };

  const closePost = async (post) => {
    try {
      const response = await axios.post(`${API}/marketplace/opportunities/${post.id}/close`);
      await fetchPosts();
      setSelectedPost(response.data);
      toast.success("Opportunity closed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to close opportunity");
    }
  };

  const updateInterest = async (interest, status) => {
    try {
      await axios.put(`${API}/marketplace/interests/${interest.id}`, { status });
      await fetchInterests(selectedPost);
      await fetchPosts();
      toast.success(`Interest marked ${status.replaceAll("_", " ")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update interest");
    }
  };

  const sendInvite = async () => {
    if (!selectedPost?.id) return toast.error("Select an opportunity first");
    try {
      await axios.post(`${API}/marketplace/opportunities/${selectedPost.id}/invite`, {
        subcontractor_company_id: inviteForm.subcontractor_company_id || null,
        contact_email: inviteForm.contact_email || null,
      });
      setInviteForm({ subcontractor_company_id: "", contact_email: "" });
      await fetchInvites(selectedPost);
      toast.success("Invite recorded");
    } catch (error) {
      // The two frozen outbound doors answer 503 {code:"not_enabled",
      // message} -- an OBJECT detail -- and handing that straight to
      // toast.error renders "[object Object]". An operator then reads a
      // deliberate, correct refusal as a broken frontend. Routed through the
      // shared classifier, which already flattens an object detail to its
      // message and knows 503/not_enabled as `disabled`. Scoped to the two
      // send doors on purpose: the same raw-detail pattern is app-wide debt
      // (~100 sites) and is NOT this slice's business.
      toast.error(classifyWorkflowError(error).message || "Failed to send invite");
    }
  };

  const convertToBid = async (interest) => {
    try {
      const bookingForm = bookingForms[interest.id] || {};
      await axios.post(`${API}/marketplace/interests/${interest.id}/convert-to-bid`, {
        project_id: bookingForm.project_id || selectedPost?.project_id || null,
        request_end_date: bookingForm.request_end_date || selectedPost?.response_deadline,
        title: selectedPost?.title,
        description: selectedPost?.scope_of_work || selectedPost?.description,
      });
      await fetchInterests(selectedPost);
      toast.success("Bid package created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bid package");
    }
  };

  const convertToProcurement = async (interest) => {
    try {
      const bookingForm = bookingForms[interest.id] || {};
      await axios.post(`${API}/marketplace/interests/${interest.id}/convert-to-procurement`, {
        project_id: bookingForm.project_id || selectedPost?.project_id || null,
        name: selectedPost?.title,
        description: selectedPost?.scope_of_work || selectedPost?.description,
        category: selectedPost?.trade,
        unit_cost: interest.proposed_amount || selectedPost?.estimated_value_min || 0,
        expected_delivery: bookingForm.request_end_date || selectedPost?.end_date,
      });
      await fetchInterests(selectedPost);
      toast.success("Procurement item created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create procurement item");
    }
  };

  const convertToBooking = async (interest) => {
    try {
      const bookingForm = bookingForms[interest.id] || {};
      await axios.post(`${API}/marketplace/interests/${interest.id}/convert-to-booking`, {
        project_id: bookingForm.project_id || selectedPost?.project_id || null,
        request_start_date: bookingForm.request_start_date || interest.availability_start || selectedPost?.start_date,
        request_end_date: bookingForm.request_end_date || interest.availability_end || selectedPost?.end_date,
        title: selectedPost?.title,
        description: selectedPost?.scope_of_work || selectedPost?.description,
        notes: bookingForm.notes || "",
      });
      await fetchInterests(selectedPost);
      toast.success("Direct booking request created");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking request");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Briefcase className="h-7 w-7" /> Market Opportunity Marketplace</h1>
        <p className="text-muted-foreground">Post subcontractor-facing opportunities, collect interest, and move qualified subs toward bookings or bids.</p>
      </div>

      <Tabs defaultValue="posts" className="space-y-6">
        <TabsList>
          <TabsTrigger value="posts">Posted Opportunities</TabsTrigger>
          <TabsTrigger value="create">Create Post</TabsTrigger>
          <TabsTrigger value="responses">Responses</TabsTrigger>
          <TabsTrigger value="invites">Invites</TabsTrigger>
        </TabsList>

        <TabsContent value="posts" className="space-y-4">
          <Card>
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
              <Input placeholder="Search title/scope/trade" value={filters.search} onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))} />
              <select className="rounded-md border border-input bg-muted/50 px-3 py-2 text-sm" value={filters.status} onChange={(e) => setFilters((current) => ({ ...current, status: e.target.value }))}>
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="closed">Closed</option>
                <option value="awarded">Awarded</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select className="rounded-md border border-input bg-muted/50 px-3 py-2 text-sm" value={filters.project_id} onChange={(e) => setFilters((current) => ({ ...current, project_id: e.target.value }))}>
                <option value="">All projects</option>
                {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              </select>
              <Input placeholder="Trade" value={filters.trade} onChange={(e) => setFilters((current) => ({ ...current, trade: e.target.value }))} />
              <Input placeholder="State" value={filters.state} onChange={(e) => setFilters((current) => ({ ...current, state: e.target.value }))} />
              <Button variant="outline" onClick={fetchPosts}>Apply Filters</Button>
            </CardContent>
          </Card>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            {posts.length ? posts.map((post) => (
              <Card key={post.id} className={`cursor-pointer ${selectedPost?.id === post.id ? "ring-2 ring-primary" : ""}`} onClick={() => setSelectedPost(post)}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-lg">{post.title}</CardTitle>
                      <CardDescription>{post.trade || "General"} {post.csi_code ? `• ${post.csi_code}` : ""}</CardDescription>
                      <CardDescription>{post.project_id ? projectName(post.project_id) : "No linked project"}</CardDescription>
                    </div>
                    <Badge variant="outline" className="capitalize">{post.status}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <div>{post.location_text || [post.city, post.state].filter(Boolean).join(", ") || "Location not set"}</div>
                  <div>Deadline: {normalizeDateTime(post.response_deadline)}</div>
                  <div>{post.interest_count || 0} response{post.interest_count === 1 ? "" : "s"}</div>
                  <div className="flex gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); startEditPost(post); }}>Edit</Button>
                    {post.status !== "published" && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); publishPost(post); }}><Send className="mr-1 h-3 w-3" /> Publish</Button>}
                    {post.status === "published" && <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); closePost(post); }}><XCircle className="mr-1 h-3 w-3" /> Close</Button>}
                  </div>
                </CardContent>
              </Card>
            )) : (
              <Card className="xl:col-span-3"><CardContent className="p-8 text-center text-muted-foreground">No marketplace opportunities yet. Create the first post.</CardContent></Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="create">
          <Card>
            <CardHeader>
              <CardTitle>{editingPostId ? "Edit Marketplace Opportunity" : "Create Marketplace Opportunity"}</CardTitle>
              <CardDescription>This creates a subcontractor-facing opportunity post. Expressed interest is not an award or contract.</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={(e) => submitPost(e, false)}>
                <div className="space-y-2 md:col-span-2"><Label>Linked Project</Label><select className="w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm" value={form.project_id} onChange={(e) => handleChange("project_id", e.target.value)}><option value="">No linked project yet</option>{projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}</select></div>
                <div className="space-y-2 md:col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e) => handleChange("title", e.target.value)} required /></div>
                <div className="space-y-2 md:col-span-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => handleChange("description", e.target.value)} /></div>
                <div className="space-y-2 md:col-span-2"><Label>Scope of Work</Label><Textarea value={form.scope_of_work} onChange={(e) => handleChange("scope_of_work", e.target.value)} /></div>
                <div className="space-y-2"><Label>Trade</Label><Input value={form.trade} onChange={(e) => handleChange("trade", e.target.value)} placeholder="Electrical, concrete, HVAC..." /></div>
                <div className="space-y-2"><Label>CSI Code</Label><Input value={form.csi_code} onChange={(e) => handleChange("csi_code", e.target.value)} placeholder="26 00 00" /></div>
                <div className="space-y-2 md:col-span-2"><Label>Location</Label><Input value={form.location_text} onChange={(e) => handleChange("location_text", e.target.value)} /></div>
                <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => handleChange("city", e.target.value)} /></div>
                <div className="space-y-2"><Label>State</Label><Input value={form.state} onChange={(e) => handleChange("state", e.target.value)} /></div>
                <div className="space-y-2"><Label>Estimated Value Min</Label><Input type="number" value={form.estimated_value_min} onChange={(e) => handleChange("estimated_value_min", e.target.value)} /></div>
                <div className="space-y-2"><Label>Estimated Value Max</Label><Input type="number" value={form.estimated_value_max} onChange={(e) => handleChange("estimated_value_max", e.target.value)} /></div>
                <div className="space-y-2"><Label>Start Date</Label><Input type="datetime-local" value={form.start_date} onChange={(e) => handleChange("start_date", e.target.value)} /></div>
                <div className="space-y-2"><Label>End Date</Label><Input type="datetime-local" value={form.end_date} onChange={(e) => handleChange("end_date", e.target.value)} /></div>
                <div className="space-y-2"><Label>Response Deadline</Label><Input type="datetime-local" value={form.response_deadline} onChange={(e) => handleChange("response_deadline", e.target.value)} /></div>
                <div className="space-y-2"><Label>Visibility</Label><select className="w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm" value={form.visibility} onChange={(e) => handleChange("visibility", e.target.value)}><option value="public">Public marketplace</option><option value="partner_network">Partner network</option><option value="invited_only">Invited only</option></select></div>
                <div className="md:col-span-2 flex gap-2 pt-2">
                  <Button type="submit" disabled={loading}>{editingPostId ? "Save Changes" : "Save Draft"}</Button>
                  <Button type="button" variant="outline" disabled={loading} onClick={(e) => submitPost(e, true)}>{editingPostId ? "Save & Publish" : "Publish Now"}</Button>
                  {editingPostId && <Button type="button" variant="ghost" onClick={resetForm}>Cancel Edit</Button>}
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="responses">
          <Card>
            <CardHeader>
              <CardTitle>Responses {selectedPost ? `• ${selectedPost.title}` : ""}</CardTitle>
              <CardDescription>Select an opportunity from Posted Opportunities to review subcontractor interest.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {interests.length ? interests.map((interest) => (
                <div key={interest.id} className="border rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{interest.subcontractor_company_name || "Subcontractor"}</div>
                      <div className="text-sm text-muted-foreground">Submitted {normalizeDateTime(interest.submitted_at || interest.created_at)}</div>
                    </div>
                    <Badge variant="outline" className="capitalize">{interest.status.replaceAll("_", " ")}</Badge>
                  </div>
                  {interest.message && <p className="text-sm">{interest.message}</p>}
                  <div className="text-sm text-muted-foreground">
                    Availability: {normalizeDateTime(interest.availability_start)} → {normalizeDateTime(interest.availability_end)}
                    {interest.proposed_amount ? ` • Proposed: $${Number(interest.proposed_amount).toLocaleString()}` : ""}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => updateInterest(interest, "shortlisted")}><Eye className="mr-1 h-3 w-3" /> Shortlist</Button>
                    <Button size="sm" variant="outline" onClick={() => updateInterest(interest, "accepted_by_gc")}><CheckCircle2 className="mr-1 h-3 w-3" /> Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => updateInterest(interest, "declined_by_gc")}><XCircle className="mr-1 h-3 w-3" /> Decline</Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 rounded-lg bg-muted/30 p-3">
                    <div className="space-y-2">
                      <Label>Project</Label>
                      <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={bookingForms[interest.id]?.project_id || selectedPost?.project_id || ""} onChange={(e) => setBookingForms((current) => ({ ...current, [interest.id]: { ...(current[interest.id] || {}), project_id: e.target.value } }))}>
                        <option value="">Select project</option>
                        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Booking Start</Label>
                      <Input type="datetime-local" value={bookingForms[interest.id]?.request_start_date || ""} onChange={(e) => setBookingForms((current) => ({ ...current, [interest.id]: { ...(current[interest.id] || {}), request_start_date: e.target.value } }))} />
                    </div>
                    <div className="space-y-2">
                      <Label>Booking End</Label>
                      <Input type="datetime-local" value={bookingForms[interest.id]?.request_end_date || ""} onChange={(e) => setBookingForms((current) => ({ ...current, [interest.id]: { ...(current[interest.id] || {}), request_end_date: e.target.value } }))} />
                    </div>
                    <div className="flex items-end">
                      <Button className="w-full" variant="outline" onClick={() => convertToBooking(interest)}>Create Direct Request</Button>
                    </div>
                    <div className="md:col-span-4 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => convertToBid(interest)}>Convert to Bid Package</Button>
                      <Button size="sm" variant="outline" onClick={() => convertToProcurement(interest)}>Convert to Procurement Item</Button>
                    </div>
                    <p className="md:col-span-4 text-xs text-muted-foreground">Creates an existing portal Direct Request, bid package, or procurement item from this marketplace response. If dates are blank, Omnas uses the subcontractor availability or post dates.</p>
                  </div>
                </div>
              )) : <p className="text-sm text-muted-foreground">No responses yet for the selected opportunity.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites">
          <Card>
            <CardHeader>
              <CardTitle>Targeted Invites {selectedPost ? `• ${selectedPost.title}` : ""}</CardTitle>
              <CardDescription>Invite known subcontractors to this opportunity. Invites also unlock visibility for invited-only posts.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 rounded-lg bg-muted/30 p-3">
                <select className="rounded-md border border-input bg-background px-3 py-2 text-sm" value={inviteForm.subcontractor_company_id} onChange={(e) => setInviteForm((current) => ({ ...current, subcontractor_company_id: e.target.value }))}>
                  <option value="">Select known subcontractor</option>
                  {contractors.map((contractor) => <option key={contractor.id} value={contractor.id}>{contractor.name}</option>)}
                </select>
                <Input placeholder="Or contact email" value={inviteForm.contact_email} onChange={(e) => setInviteForm((current) => ({ ...current, contact_email: e.target.value }))} />
                <Button onClick={sendInvite} disabled={!selectedPost}>Record Invite</Button>
              </div>
              {invites.length ? invites.map((invite) => (
                <div key={invite.id} className="border rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{invite.subcontractor_company_name || invite.contact_email || "Invite"}</div>
                      <div className="text-sm text-muted-foreground">{invite.contact_email || "No email"} • {invite.sent_at ? new Date(invite.sent_at).toLocaleString() : ""}</div>
                    </div>
                    <Badge variant="outline" className="capitalize">{invite.status}</Badge>
                  </div>
                  {invite.invite_url && <div className="flex flex-col md:flex-row gap-2"><Input readOnly value={invite.invite_url} /><Button variant="outline" onClick={() => navigator.clipboard.writeText(invite.invite_url)}>Copy Link</Button></div>}
                </div>
              )) : <p className="text-sm text-muted-foreground">No invites recorded for the selected opportunity.</p>}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketplacePage;
